/* dashboard/kpi_mlb.js — pure data shaping for MLB premium KPI tiles.
 *
 * Extracted from static/js/live.js on 2026-05-31 (Phase L2 of the
 * live.js refactor, see memory [[user-dashboards-architecture]]). The
 * old getters/methods inside dashboard() are now thin delegations of the
 * form
 *   get _mlbMyBombs()     { return LSKpiMlb.myBombs(this); }
 *   _mlbTopScorer(g)      { return LSKpiMlb.topScorer(this, g); }
 *
 * Template + widget API is unchanged — kpi/mlb_*.js still read
 * dashboard._mlbMyBombs / dashboard._mlbPitcherLines / etc.
 *
 * Why extract: ~160 lines of MLB-specific reductions sat inside the
 * sport-agnostic controller. Sport-branch leakage in live.js is a
 * known anti-pattern (see plan in [[user-dashboards-architecture]]
 * Phase L4 — "every sport-branch moves into a sport-specific
 * dashboard module"). This file is that module for MLB premium KPIs.
 *
 * Player stat shape (matches batter_pulse / pitcher_pulse):
 *   p.stats.{hr, rbi, r, sb, bb, so, h, ab}     - hitters
 *   p.stats.{ip, er, so, h, bb, pitches}        - pitchers
 *   p.live_fpts                                  - both
 *
 * Dedup rule: every "my X" metric counts a player ONCE even if they
 * appear in 50 lineups — 1 HR = 1 bomb, not 50 bombs. Key is
 * player_id with name|team fallback.
 *
 * Public API (all functions take `ctx` first; per-call args follow):
 *   topScorer(ctx, g)     -> {name, live_fpts, ...} | null
 *   myBombs(ctx)          -> number
 *   pitcherLines(ctx)     -> {ip, er, sps, top_fpts, top_name}
 *   cooking(ctx)          -> {count, top_name, top_line}
 *   sweatMeter(ctx)       -> {live, total, pct}
 *   stackLeader(ctx)      -> {team, fpts, size, batted} | null
 *   paddedInnings(ctx, g) -> [{n, a, h}, ...]   (always >= 9 entries)
 *
 * ctx fields consumed:
 *   ctx.lineupRows      [{players: [{player_id, name, team, pos,
 *                          live_fpts, stats, game_id}, ...]}, ...]
 *   ctx.allGames        [{game_id, status, live_state: {innings: [...]}}, ...]
 *   ctx.allSlatePlayers [{game_id, live_fpts, ...}, ...]
 */

window.LSKpiMlb = (function () {

  const PITCHER = new Set(['SP', 'P', 'RP']);

  function _playerKey(p) {
    return String(p.player_id || p.external_player_id || '');
  }

  // Total hits for a hitter. The boxscore ships single/double/triple/hr, not a
  // precomputed `h` — matches batter_pulse's derivation.
  function _hits(s) {
    s = s || {};
    return (s.single || 0) + (s.double || 0) + (s.triple || 0) + (s.hr || 0);
  }

  /** Highest-scoring DFS player in an MLB game. Used by the scoreboard
   *  card foot to show a "Top scorer" line per game. Returns null if
   *  no players from that game have logged any FPts yet (early-game
   *  state). Sort by live_fpts desc, take first. */
  function topScorer(ctx, g) {
    if (!g || !g.game_id) return null;
    const players = (ctx.allSlatePlayers || [])
      .filter(p => p.game_id === g.game_id && (p.live_fpts || 0) > 0);
    if (!players.length) return null;
    return players.reduce((best, p) =>
      (p.live_fpts || 0) > (best.live_fpts || 0) ? p : best,
    );
  }

  /** Total HRs across my lineups (deduplicated by player). One HR
   *  hit by a player rostered in 50 lineups still counts as 1 — the
   *  metric is "how many HRs happened to me", not weighted by
   *  exposure. */
  function myBombs(ctx) {
    const seen = new Set();
    let total = 0;
    for (const lu of (ctx.lineupRows || [])) {
      for (const p of (lu.players || [])) {
        const k = _playerKey(p);
        if (seen.has(k)) continue;
        seen.add(k);
        total += (p.stats?.hr || 0);
      }
    }
    return total;
  }

  /** Aggregate pitcher line across my unique SPs.
   *  Returns { ip, er, sps, top_fpts, top_name } */
  function pitcherLines(ctx) {
    const seen = new Set();
    let outs = 0, er = 0, sps = 0, topFpts = -1, topName = '';
    for (const lu of (ctx.lineupRows || [])) {
      for (const p of (lu.players || [])) {
        if (!PITCHER.has(p.pos)) continue;
        const k = _playerKey(p);
        if (seen.has(k)) continue;
        seen.add(k);
        sps++;
        // IP is baseball notation ("5.2" = 5⅔ innings = 17 outs), NOT a decimal —
        // it must be summed as OUTS, then re-formatted, or "5.2"+"4.2" reads 9.4.
        const [w, f] = String(p.stats?.ip ?? 0).split('.');
        outs += (+w || 0) * 3 + (+f || 0);
        er += (p.stats?.er || 0);
        if ((p.live_fpts || 0) > topFpts) {
          topFpts = p.live_fpts || 0;
          topName = p.name || '';
        }
      }
    }
    const ip = Math.floor(outs / 3) + (outs % 3) / 10;   // outs → whole.remainder
    return { ip, er, sps, top_fpts: topFpts, top_name: topName };
  }

  /** Count of my hitters with 2+ hits ("cooking" threshold) +
   *  the current leader by FPts among them.
   *  Returns { count, top_name, top_line } */
  function cooking(ctx) {
    const seen = new Set();
    const cookers = [];
    for (const lu of (ctx.lineupRows || [])) {
      for (const p of (lu.players || [])) {
        if (PITCHER.has(p.pos)) continue;
        const k = _playerKey(p);
        if (seen.has(k)) continue;
        seen.add(k);
        // Hitters ship single/double/triple/hr — NOT a precomputed `h`. Reading
        // p.stats.h (undefined) left this tile permanently at 0. Derive hits.
        if (_hits(p.stats) >= 2) cookers.push(p);
      }
    }
    if (!cookers.length) return { count: 0, top_name: '', top_line: '' };
    const top = cookers.reduce((a, b) =>
      (a.live_fpts || 0) >= (b.live_fpts || 0) ? a : b,
    );
    const h = _hits(top.stats), ab = top.stats?.ab || 0;
    return {
      count: cookers.length,
      top_name: top.name || '',
      top_line: `${h}-for-${ab}`,
    };
  }

  /** Sweat meter — % of my unique player rosters still in unfinished
   *  games. Counts down from ~100% at slate start to 0% at end.
   *  Returns { live, total, pct } */
  function sweatMeter(ctx) {
    const gameStatusById = {};
    for (const g of (ctx.allGames || [])) {
      // effStatus, not raw status: a game whose provider is stuck at 2 past its
      // end window resolves to 3 here, so its players stop counting as "still
      // sweating" — otherwise the meter never fully drains.
      gameStatusById[g.game_id] = ctx.effStatus(g);
    }
    const seen = new Set();
    let total = 0, live = 0;
    for (const lu of (ctx.lineupRows || [])) {
      for (const p of (lu.players || [])) {
        const k = _playerKey(p);
        if (seen.has(k)) continue;
        seen.add(k);
        total++;
        const status = gameStatusById[p.game_id];
        // status 2 = LIVE, 1 = SOON. Both count as "still has FPts
        // to earn". Status 3 = FINAL.
        if (status === 1 || status === 2) live++;
      }
    }
    return { live, total, pct: total > 0 ? (live / total * 100) : 100 };
  }

  /** Best-performing stack across my lineups. A stack = 2+ hitters
   *  from the same team in the same lineup. Returns { team, fpts,
   *  size, batted } or null if no stacks. */
  function stackLeader(ctx) {
    let best = null;
    for (const lu of (ctx.lineupRows || [])) {
      const byTeam = {};
      for (const p of (lu.players || [])) {
        if (!p.team || PITCHER.has(p.pos)) continue;
        const t = p.team.toUpperCase();
        if (!byTeam[t]) byTeam[t] = [];
        byTeam[t].push(p);
      }
      for (const [team, players] of Object.entries(byTeam)) {
        if (players.length < 2) continue;
        const fpts   = players.reduce((s, p) => s + (p.live_fpts || 0), 0);
        const batted = players.filter(p => (p.stats?.ab || 0) > 0).length;
        if (!best || fpts > best.fpts) {
          best = { team, fpts, size: players.length, batted };
        }
      }
    }
    return best;
  }

  /** Pad an MLB game's innings array to always have at least 9
   *  entries (more if extra-innings). Missing inning slots get
   *  {n, a: null, h: null} so the template can render `·` placeholders
   *  consistently. Locked 2026-05-27 — scoreboard cards must always
   *  show 1–9 columns for layout stability across games at different
   *  states. Called from templates/widgets/scoreboard_mlb.html. */
  function paddedInnings(_ctx, g) {
    const got = (g && g.live_state && g.live_state.innings) || [];
    // Pre-game / no innings yet: still return a 1–9 placeholder grid so a
    // full-mode card shows the line-score structure regardless of status
    // (all cells blank). Keeps scheduled cards structurally identical to
    // live ones rather than collapsing the strip.
    if (!got.length) {
      return Array.from({length: 9}, (_, i) => ({ n: i + 1, a: null, h: null }));
    }
    const maxN = Math.max(9, ...got.map(i => i.n));
    const by_n = {};
    got.forEach(i => { by_n[i.n] = i; });
    return Array.from({length: maxN}, (_, i) =>
      by_n[i+1] || {n: i+1, a: null, h: null},
    );
  }

  return {
    topScorer,
    myBombs,
    pitcherLines,
    cooking,
    sweatMeter,
    stackLeader,
    paddedInnings,
  };
})();
